import type { ReactNode } from 'react';
import { Link } from '@backstage/core-components';
import { LinearProgress, makeStyles } from '@material-ui/core';
import { formatStarted } from '../pipeline/format';
import { outcomeOf } from '../pipeline/outcome';
import { OutcomePill } from '../pipeline/OutcomePill';
import { useComponentPage } from './ComponentPageContext';
import {
  formatUptime,
  PROBE_HOW_TO,
  STATUS_COLOUR,
  STATUS_LABEL,
} from './serviceStatus';
import { scorecardLinkOf } from '../../scorecards/scorecards';
import {
  ACCESS_PAGE,
  SEVERITY_COLOUR,
  SEVERITY_SHAPE,
  securityTabOf,
  unmeasuredReasonOf,
} from '../../security/security';
import { Shape } from '../../security/SecurityParts';

const useStyles = makeStyles(theme => ({
  bar: {
    // Cells keep their width rather than stretching: a component with one
    // fact shows one compact cell, not a bar across the page with one number.
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 280px))',
    width: 'fit-content',
    maxWidth: '100%',
    // On a phone two to a line, rather than one per line down the screen.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      width: '100%',
    },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  cell: {
    padding: theme.spacing(1.25, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
  },
  label: { fontSize: '0.75rem', color: 'var(--ui-text-muted)' },
  value: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    minWidth: 0,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flex: '0 0 auto',
  },
  note: {
    fontSize: '0.75rem',
    fontWeight: 400,
  },
  sub: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    fontWeight: 400,
    overflowWrap: 'anywhere',
  },
}));

const Cell = ({ label, children }: { label: string; children: ReactNode }) => {
  const classes = useStyles();
  return (
    <div className={classes.cell} role="group" aria-label={label}>
      <div className={classes.label}>{label}</div>
      <div className={classes.value}>{children}</div>
    </div>
  );
};

const Loading = () => (
  <LinearProgress
    aria-label="Loading"
    style={{ width: 90, height: 4, borderRadius: 2, margin: '8px 0' }}
  />
);
const CouldNotLoad = () => <span>Couldn't load</span>;

/**
 * The facts bar (specs/component-page.md): the few facts a colleague opens a
 * component's page to learn, each from where it is known.
 *
 * A cell with no source is left out rather than filled: the bar never shows a
 * placeholder figure. Health and uptime come from the status service, and only
 * for what its probes measured — never its stand-in for "nothing measured".
 */
export const FactsBar = () => {
  const classes = useStyles();
  const { slug, pipeline, catalog, status, scorecard, security } =
    useComponentPage();
  const cells: ReactNode[] = [];
  const hasDeployWorkflow =
    !!pipeline.data && pipeline.data.deployWorkflows.length > 0;

  // Health and uptime — from the status service's probes. A component it
  // measures shows both; one it knows without measurements, or a deployed one
  // it does not know, says there is nothing measured and how to change that;
  // one with neither a probe nor a deploy workflow has no cell at all.
  if (status.loading) {
    cells.push(
      <Cell key="health" label="Health">
        <Loading />
      </Cell>,
    );
  } else if (status.error) {
    cells.push(
      <Cell key="health" label="Health">
        <CouldNotLoad />
      </Cell>,
    );
  } else if (status.data && status.data.uptimePct !== null) {
    const service = status.data;
    const uptime = status.data.uptimePct;
    const incidents = service.incidents.inWindow;
    cells.push(
      <Cell key="health" label="Health">
        <span
          className={classes.dot}
          style={{ background: STATUS_COLOUR[service.status] }}
          aria-hidden
        />
        <span>{STATUS_LABEL[service.status]}</span>
        {service.status === 'no_data' && (
          <span className={classes.sub}>nothing in 24 h</span>
        )}
      </Cell>,
      <Cell key="uptime" label={`Uptime · ${service.windowDays} days`}>
        <span>{formatUptime(uptime)}</span>
        <span className={classes.sub}>
          {incidents === 0
            ? 'no incidents'
            : `${incidents} incident${incidents === 1 ? '' : 's'}`}
        </span>
      </Cell>,
    );
  } else if (status.data || hasDeployWorkflow) {
    cells.push(
      <Cell key="health" label="Health">
        <span>No probe data</span>
        <Link to={PROBE_HOW_TO} className={classes.note}>
          Add a probe
        </Link>
      </Cell>,
    );
  }

  // Last deploy — only for a repository that has a deploy workflow at all; a
  // library that never deploys has no cell rather than an empty one.
  if (slug) {
    if (pipeline.loading) {
      cells.push(
        <Cell key="deploy" label="Last deploy">
          <Loading />
        </Cell>,
      );
    } else if (pipeline.error) {
      cells.push(
        <Cell key="deploy" label="Last deploy">
          <CouldNotLoad />
        </Cell>,
      );
    } else if (pipeline.data && pipeline.data.deployWorkflows.length > 0) {
      const run = pipeline.data.latestDeployment;
      const started = run
        ? formatStarted(run.createdAt, Date.now())
        : undefined;
      cells.push(
        <Cell key="deploy" label="Last deploy">
          {run ? (
            <>
              <span title={started?.exact}>{started?.label ?? 'Unknown'}</span>
              <OutcomePill outcome={outcomeOf(run)} />
              <span className={classes.sub}>
                {[run.headBranch, run.headSha?.slice(0, 7)]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </>
          ) : (
            <span>No deploy yet</span>
          )}
        </Cell>,
      );
    }
  }

  // Consumers — who depends on the component or calls its API. Nobody is a
  // fact, so it reads 0 rather than disappearing.
  cells.push(
    <Cell key="consumers" label="Consumers">
      {catalog.loading && <Loading />}
      {!catalog.loading && catalog.error && <CouldNotLoad />}
      {!catalog.loading && !catalog.error && (
        <span>{catalog.relations?.inbound.length ?? 0}</span>
      )}
    </Cell>,
  );

  // API — the first API the component provides, with its version.
  const [api, ...more] = catalog.providedApis;
  if (api) {
    cells.push(
      <Cell key="api" label="API">
        <span>{api.title ?? api.name}</span>
        {api.version && <span className={classes.sub}>v{api.version}</span>}
        {more.length > 0 && <span className={classes.sub}>+{more.length}</span>}
      </Cell>,
    );
  }

  // Security — the component's row on the Security page: its most severe open
  // finding, that none is open, or that it is not measured, and why
  // (specs/security-findings.md AC-10). Only a component that names its
  // repository has one; the cell never shows a number it was not given.
  if (security) {
    const row = security.data?.component;
    let value: ReactNode;
    let sub: string | undefined;
    if (security.loading) value = <Loading />;
    else if (security.refused) {
      value = <Link to={ACCESS_PAGE}>Team members only</Link>;
    } else if (security.error || !security.data) value = <CouldNotLoad />;
    else if (!security.data.generatedAt || !row) {
      value = <span>Not read yet</span>;
      // Read, but not this component: it named its repository since.
      if (security.data.generatedAt) sub = 'Read with the next hourly read';
    } else {
      const read = row.readings.filter(r => r.state === 'read').length;
      const sources = `${read} ${read === 1 ? 'source' : 'sources'}`;
      const tab = securityTabOf(row.entityRef);
      if (row.status === 'open' && row.worst) {
        value = (
          <Link to={tab}>
            <Shape
              shape={SEVERITY_SHAPE[row.worst.severity]}
              colour={SEVERITY_COLOUR[row.worst.severity]}
            />{' '}
            {row.worst.count} {row.worst.severity}
          </Link>
        );
        sub = `${row.open} open · ${sources}`;
      } else if (row.status === 'none') {
        value = <Link to={tab}>None open</Link>;
        sub = `${sources} read`;
      } else {
        value = <Link to={tab}>Not measured</Link>;
        sub = unmeasuredReasonOf(row);
      }
    }
    cells.push(
      <Cell key="security" label="Security">
        {value}
        {sub && <span className={classes.sub}>{sub}</span>}
      </Cell>,
    );
  }

  // Scorecard — a service's row on Scorecards, as the backend scored it, last
  // in the bar because it sums up what the checks found
  // (specs/scorecards.md AC-7). Only a service has one; until the checks have
  // run, or when they cannot be loaded, the cell says which, never a number.
  if (scorecard) {
    const row = scorecard.data?.service;
    let value: ReactNode;
    if (scorecard.loading) value = <Loading />;
    else if (scorecard.error || !scorecard.data) value = <CouldNotLoad />;
    else if (!scorecard.data.generatedAt || !row) {
      value = <span>Not checked yet</span>;
    } else {
      const failing = row.asked - row.passed;
      value = (
        <>
          <Link
            to={scorecardLinkOf(row.entityRef)}
            aria-label={`${row.passed} of ${row.asked} checks passed`}
          >
            {row.passed}/{row.asked}
          </Link>
          <span className={classes.sub}>
            {failing === 0
              ? 'all pass'
              : `${failing} ${failing === 1 ? 'check fails' : 'checks fail'}`}
          </span>
        </>
      );
    }
    cells.push(
      <Cell key="scorecard" label="Scorecard">
        {value}
      </Cell>,
    );
  }

  return (
    <div className={classes.bar} role="group" aria-label="Facts">
      {cells}
    </div>
  );
};
